import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'quiet' | 'danger';
export type ButtonSize = 'sm' | 'md' | 'lg';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Optional leading icon; decorative and hidden from assistive tech. */
  icon?: ReactNode;
  /** Stretch to the container width. */
  block?: boolean;
}

const VARIANT: Record<ButtonVariant, string> = {
  primary:
    'bg-accent text-white shadow-card hover:bg-accent-strong active:bg-accent-strong',
  secondary:
    'border border-line-strong bg-surface text-ink shadow-card hover:bg-surface-sunken active:bg-surface-sunken',
  ghost:
    'border border-transparent bg-transparent text-ink-soft hover:bg-navy-soft hover:text-ink',
  quiet:
    'bg-accent-soft text-accent-strong hover:bg-accent-soft/80 active:bg-accent-soft',
  danger:
    'border border-danger/30 bg-danger-soft text-danger hover:bg-danger/10 active:bg-danger/15',
};

const SIZE: Record<ButtonSize, string> = {
  sm: 'h-8 px-3 text-xs gap-1.5',
  md: 'h-10 px-4 text-sm gap-2',
  lg: 'h-12 px-6 text-base gap-2',
};

/**
 * The single action primitive. Pill-shaped by default to match the product's
 * design language. All native button attributes pass through, so role, type,
 * disabled, and aria wiring used by tests keep working unchanged.
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'primary',
    size = 'md',
    icon,
    block = false,
    className = '',
    type = 'button',
    children,
    ...rest
  },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      className={[
        'inline-flex items-center justify-center rounded-pill font-semibold',
        'transition-[background-color,box-shadow,transform,color] duration-200 ease-out',
        'disabled:cursor-not-allowed disabled:opacity-55 disabled:shadow-none',
        VARIANT[variant],
        SIZE[size],
        block ? 'w-full' : '',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      {...rest}
    >
      {icon ? (
        <span aria-hidden="true" className="shrink-0 [&>svg]:h-4 [&>svg]:w-4">
          {icon}
        </span>
      ) : null}
      {children}
    </button>
  );
});

interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Required for accessibility: the button has no visible text. */
  label: string;
  variant?: ButtonVariant;
  icon: ReactNode;
}

export function IconButton({
  label,
  variant = 'ghost',
  icon,
  className = '',
  type = 'button',
  ...rest
}: IconButtonProps) {
  return (
    <button
      type={type}
      aria-label={label}
      title={label}
      className={[
        'inline-flex h-8 w-8 items-center justify-center rounded-pill',
        'transition-[background-color,box-shadow,color] duration-200 ease-out',
        'disabled:cursor-not-allowed disabled:opacity-55',
        VARIANT[variant],
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      {...rest}
    >
      <span aria-hidden="true" className="[&>svg]:h-4 [&>svg]:w-4">
        {icon}
      </span>
    </button>
  );
}
